// frontend/src/components/RoomInfoViewer.tsx
import React, { useMemo, useState, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import type { ColDef } from "ag-grid-enterprise";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { Card, Tabs, Box, ScrollArea, Paper, Flex, ActionIcon, Tooltip } from "@mantine/core";
import { FaTable, FaCode, FaRedo } from "react-icons/fa";
import { IRoom } from "@multiconsult-group/multiplacer-core";
import { EnrichSingleRoomButton } from "../DesignTab/EnrichSingleRoomButton";
import { openapi } from "../../openapi";
import { selectIsSystemAdmin } from "../userSelectors";
import { selectHttpSingleRoomFull } from "../Room/httpRoomSelectors";
import { selectActiveCeilingHeightAboveFloorMm } from "../McBim/mcbimSelectors";

type IRoomPropertyRow = {
  property: string;
  value: string;
  source?: string;
};

export const RoomInfoViewer: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"grid" | "json">("grid");
  const gridRef = useRef<AgGridReact<IRoomPropertyRow>>(null);

  const dispatch = useAppDispatch();

  const room = useAppSelector(selectHttpSingleRoomFull);
  const activeCeilingHeightAboveFloorMm = useAppSelector(selectActiveCeilingHeightAboveFloorMm);

  // ==================== TABELL-DATA ====================

  const rows: IRoomPropertyRow[] = useMemo(() => {
    if (room == null) return [];
    const data: IRoomPropertyRow[] = [];

    const addRow = (property: string, value: unknown, source?: string) => {
      if (value === undefined || value === null || value === "") return;

      let v: string;
      if (typeof value === "string") v = value;
      else if (typeof value === "number") v = value.toString();
      else if (value instanceof Date) v = value.toISOString();
      else v = JSON.stringify(value);

      data.push({ property, value: v, source });
    };

    addRow("Nummer", room.roomNumber, "IFC");
    addRow("Navn", room.roomName, "IFC");
    addRow("Tilluft", room.supplyAirFlow, "Excel");
    addRow("Avtrekk", room.returnAirFlow, "Excel");
    addRow("RFP malrom", room.rfpMalRom, "RFP");
    addRow("RFP status", room.rfpStatus, "RFP");
    addRow("PNS", room.extraIfcInfo?.pns, "IFC");
    addRow("Himling over gulv", activeCeilingHeightAboveFloorMm, "MCBIM");

    // 👇 Ny rad: ML-predikerte devices (antall)
    if (Array.isArray(room.mlPredictedDevices)) {
      addRow("ML-predikerte devices", room.mlPredictedDevices.length, "ML-tjeneste");
    }

    addRow("Oppdatert", room.updatedAt, "MCApps");
    if (room.multiplacerCeilingDevicesLocked) {
      addRow("Låst", room.multiplacerCeilingDevicesLocked.bool ? "Ja" : "Nei", "MCApps");
      addRow("Låst dato", room.multiplacerCeilingDevicesLocked.lockedAt, "MCApps");
    }

    return data;
  }, [activeCeilingHeightAboveFloorMm, room]);

  const columnDefs = useMemo<ColDef<IRoomPropertyRow>[]>(
    () => [
      {
        field: "property",
        headerName: "Egenskap",
        flex: 1,
        sortable: true,
      },
      {
        field: "value",
        headerName: "Verdi",
        flex: 1.5,
      },
      // {
      //   field: "source",
      //   headerName: "Kilde",
      //   width: 170,
      // },
    ],
    []
  );

  const defaultColDef = useMemo<ColDef<IRoomPropertyRow>>(
    () => ({
      resizable: true,
    }),
    []
  );

  // ==================== JSON-VY ====================
  const prettyJson = useMemo(() => JSON.stringify(room as IRoom, null, 2), [room]);

  const invalidateSingleRoom = () => {
    if (!room) return;
    dispatch(openapi.util.invalidateTags(["SingleRoom"]));
  };

  const userIsAdmin = useAppSelector(selectIsSystemAdmin);

  return (
    <Card withBorder radius="md" w="100%">
      <Flex gap="xs">
        {userIsAdmin && <EnrichSingleRoomButton />}
        {userIsAdmin && (
          <Tooltip label="Oppdater rom" openDelay={400}>
            <ActionIcon
              variant="outline"
              size="sm"
              onClick={invalidateSingleRoom}
              disabled={!room}
              aria-label="Oppdater rom"
            >
              <FaRedo size={12} />
            </ActionIcon>
          </Tooltip>
        )}
      </Flex>

      <Tabs
        value={activeTab}
        onChange={(v) => v && setActiveTab(v as "grid" | "json")}
        keepMounted={false}
      >
        <Tabs.List grow mb="md">
          <Tabs.Tab value="grid" leftSection={<FaTable size={14} />}>
            Tabellvisning
          </Tabs.Tab>
          <Tabs.Tab value="json" leftSection={<FaCode size={14} />}>
            JSON-visning
          </Tabs.Tab>
        </Tabs.List>

        {/* ========== AG-GRID VY ========== */}
        <Tabs.Panel value="grid" pt={0}>
          <Box className="ag-theme-alpine" w="100%" h={600}>
            <AgGridReact<IRoomPropertyRow>
              ref={gridRef}
              rowData={rows}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              enableCellTextSelection={true}
              suppressRowClickSelection={true}
              animateRows={true}
            />
          </Box>
        </Tabs.Panel>

        {/* ========== JSON VY ========== */}
        <Tabs.Panel value="json" pt={0} w="400px">
          <ScrollArea h={400}>
            <Paper
              component="pre"
              p="md"
              radius="md"
              withBorder
              style={{
                width: "100%",
                boxSizing: "border-box",
                overflowX: "auto",
                margin: 0,
                fontFamily: "monospace",
                fontSize: 12,
                whiteSpace: "pre",
              }}
            >
              {prettyJson}
            </Paper>
          </ScrollArea>
        </Tabs.Panel>
      </Tabs>
    </Card>
  );
};
